import React, {MouseEventHandler, useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router';
import moment from 'moment';
import Modal from '../../components/Modal';
import {useTranslation} from 'react-i18next';
import {AxiosError} from 'axios';
import {Location} from 'history';
import client from 'lib/CommonApi/client';
import {
    callAlertWeb,
    clickGmoneypayDetailWeb,
    clickIncomeDeductionWeb,
    hideProgressWeb,
    showProgressWeb
} from 'common/appInterface';
import {inquiryStaticData} from '../../api/SelfCommonAPI';

type CardManageButtonProps = {
    onClick: MouseEventHandler<HTMLButtonElement>;
    disabled?: boolean;
    title: string;
    sub?: string;
    content?: string;
    subcontent?: string;
}

const CardManageButton: React.FC<CardManageButtonProps> = ({
                                                               onClick,
                                                               disabled,
                                                               title,
                                                               sub,
                                                               content,
                                                               subcontent
                                                           }) => {
    return (
        <dd>
            <button type="button" onClick={onClick} disabled={disabled}>
                <span className="tit title">{title}</span>
                {content && (<span className="content">{content}</span>)}
                {subcontent && (<span className="subcontent">{subcontent}</span>)}
                {sub && (<span className="account">{sub}</span>)}
            </button>
        </dd>
    );
};

const CardManage: React.FC<CardManageProps> = ({
                                                   cardInfo,
                                                   callbackReload,
                                                   swiperIndex,
                                                   linkOpenMenu,
                                                   nonInoutTotalBalance,
                                                   nonInoutInfoDetail,
                                                   callNonInoutInfo,
                                                   incomeDeductionAppVersion,
                                                   cardIncomeDeductionVersionApi,
                                                   callMonthlyFeeHistory,
                                                   monthlyFeeHistory,
                                                   swiperTabChangeEventHandle,
                                                   cardTerminateCancel,
                                                   openReissueModal
                                               }) => {
    /** Location State */
    const location = useLocation() as Location<LocationState>;
    /** Navigate */
    const navigate = useNavigate();
    /** 다국어처리 */
    const {t} = useTranslation();

    const [isPauseModal, setPauseModal] = useState<boolean>(false);
    const [isLostAcciModal, setLostAcciModal] = useState<boolean>(false);
    const [isLostCnclModal, setLostCnclModal] = useState<boolean>(false);

    const [cardWithdrawAmount, setCardWithdrawAmount] = useState<number>(0);
    const [debitState, setDebitState] = useState<CardDebitState | null>(null);

    const [isLinkMenu, setLinkMenu] = useState<boolean>(false);

    const [hasMonthlyFee, setHasMonthlyFee] = useState<boolean>(cardInfo.membershipFeeCode === "1");
    const [hasMonthlyFeeType, setHasMonthlyFeeType] = useState<boolean>(cardInfo.monthlyFeePayTypeCnt > 0);

    /** 일시정지 모달닫기 */
    const closePauseModal = useCallback(() => {
            setPauseModal(false);
            callbackReload();
        },
        [callbackReload]
    );

    /** 분실신고 모달닫기 */
    const closeLostAcciModal = useCallback(() => {
            setLostAcciModal(false);
            callbackReload();
        },
        [callbackReload]
    );

    /** 분실신고해제 모달닫기 */
    const closeLostCnclModal = useCallback(() => {
            setLostCnclModal(false);
            callbackReload();
        },
        [callbackReload]
    );

    /** 사고등해제 호출 */
    const callAccident = useCallback(async (code: string) => {
        showProgressWeb();
        await client.post("/api/app/accident/cardAccident", {
            ...location.state.query,
            alternativeCardNo: cardInfo.alternativeCardNo,
            cardAccidentCode: code,
            userLostDate: (code === "111" ? moment().format("YYYYMMDD") : null)
        }).then((response) => {
            // 정상
            if (response.data.code === "00") {
                // 일시정지
                if (code.startsWith("07")) {
                    setPauseModal(true);
                }
                // 분실신고
                else if (code.startsWith("111")) {
                    setLostAcciModal(false);
                    callbackReload();
                } else {
                    callbackReload();
                }
            } else {
                callAlertWeb("ERROR", response.data.code, response.data.message);
            }
        }).catch((e: AxiosError) => {
            callAlertWeb("ERROR", "99", e.message);
        }).finally(() => {
            hideProgressWeb();
        });
    }, [location.state.query, cardInfo, callbackReload]);

    /**
     * dhbyun
     * 카드 자동 출금 금액 조회
     */
    const inquiryStaticDataApi = useCallback(() => {
            inquiryStaticData(
                "CARD_WITHDRAW_ADDITIONAL_AMT",
                location
            ).then((res) => {
                if (res.data.resData) {
                    setCardWithdrawAmount(res.data.resData.data);
                }
            }).catch((e: AxiosError) => {
                callAlertWeb("ERROR", "99", e.message);
            });
        },
        [location]
    );

    /** 카드출금동의여부조회 */
    const callDebitState = useCallback(() => {
            client.get('/api/app/manage/debit/state', {
                params: {
                    ...location.state.query,
                    alternativeCardNo: cardInfo.alternativeCardNo
                }
            }).then(response => {
                setDebitState(response.data.response);
                if (linkOpenMenu) setLinkMenu(true);
            }).catch((e: AxiosError) => {
                callAlertWeb('ERROR', '99', e.message);
            });
        },
        [cardInfo.alternativeCardNo, location.state.query, linkOpenMenu, setLinkMenu]
    );

    const clickMonthlyFeeHistory = useCallback(() => {
            navigate("/manage/monthlyFee/history", {
                state: {
                    query: location.state.query,
                    info: cardInfo,
                    swiperIndex: swiperIndex,
                    tabIndex: "3",
                    userInfo: debitState,
                    storage: {
                        monthlyFeeHistory: monthlyFeeHistory,
                        nonInoutTotalBalance: nonInoutTotalBalance,
                        nonInoutInfoDetail: nonInoutInfoDetail
                    },
                    linkOpenMenu: (linkOpenMenu) ? 'main' : ''
                }
            });
        },
        [
            navigate, location.state.query, cardInfo,
            swiperIndex, debitState, monthlyFeeHistory,
            nonInoutTotalBalance, nonInoutInfoDetail, linkOpenMenu
        ]
    );

    const clickCardDebit = useCallback(() => {
            navigate("/manage/card/debit", {
                state: {
                    query: location.state.query,
                    info: cardInfo,
                    swiperIndex: swiperIndex,
                    tabIndex: "3",
                    staticInfo: {
                        cardWithdrawAdditionalAmount: cardWithdrawAmount
                    },
                    userInfo: debitState,
                    linkOpenMenu: (linkOpenMenu) ? 'main' : ''
                }
            });
        },
        [
            navigate, location.state.query, cardInfo, swiperIndex,
            cardWithdrawAmount, debitState, linkOpenMenu
        ]
    );

    const clickPasswordChange = useCallback(() => {
            navigate("/manage/password/change", {
                state: {
                    query: location.state.query,
                    info: cardInfo,
                    swiperIndex: swiperIndex,
                    tabIndex: "3",
                    linkOpenMenu: (linkOpenMenu) ? 'main' : ''
                }
            });
        },
        [navigate, location.state.query, cardInfo, swiperIndex, linkOpenMenu]
    );

    const clickFreeze = useCallback(() => {
            navigate("/manage/card/freeze", {
                state: {
                    query: location.state.query,
                    info: cardInfo,
                    swiperIndex: swiperIndex,
                    tabIndex: "3",
                    linkOpenMenu: (linkOpenMenu) ? 'main' : ''
                }
            });
        },
        [navigate, location.state.query, cardInfo, swiperIndex, linkOpenMenu]
    );

    const clickMonthlyFeeDebit = useCallback(() => {
            navigate("/manage/monthlyFee/debit", {
                state: {
                    query: location.state.query,
                    info: cardInfo,
                    swiperIndex: swiperIndex,
                    tabIndex: "3",
                    staticInfo: {
                        cardWithdrawAdditionalAmount: cardWithdrawAmount
                    },
                    userInfo: debitState,
                    linkOpenMenu: (linkOpenMenu) ? 'main' : ''
                }
            });
        },
        [
            navigate, location.state.query, cardInfo, swiperIndex,
            cardWithdrawAmount, debitState, linkOpenMenu
        ]
    );

    //카드 월회비 유형
    const clickCardMonthlyFeeTypeView = useCallback(() => {
            navigate("/manage/card/monthlyFeeType", {
                state: {
                    query: location.state.query,
                    info: cardInfo,
                    apply: {
                        callPage: 'cardManage'
                    },
                    from: location.pathname,
                    swiperIndex: swiperIndex,
                    tabIndex: "3",
                    linkOpenMenu: (linkOpenMenu) ? 'main' : ''
                }
            });
        },
        [navigate, location, cardInfo, swiperIndex, linkOpenMenu]
    );

    const clickCardNumberView = useCallback(() => {
            navigate("/manage/pincode/verify", {
                state: {
                    query: location.state.query,
                    apply: {
                        alternativeCardNo: cardInfo.alternativeCardNo
                        , callPage: 'cardManage'
                    },
                    from: location.pathname,
                    swiperIndex: swiperIndex,
                    tabIndex: "3"
                }
            });
        },
        [navigate, location, cardInfo.alternativeCardNo, swiperIndex]
    );

    const clickLostCancel = useCallback(() => {
            navigate("/manage/card/verify/lostCancel", {
                state: {
                    query: location.state.query,
                    info: cardInfo,
                    swiperIndex: swiperIndex,
                    tabIndex: "3"
                }
            });
        },
        [cardInfo, location.state.query, navigate, swiperIndex]
    );

    const clickTerminate = useCallback(() => {
            navigate('/terminate/step1', {
                state: {
                    query: location.state.query,
                    info: cardInfo,
                    swiperIndex: swiperIndex,
                    tabIndex: "3",
                    linkOpenMenu: (linkOpenMenu) ? 'main' : ''
                }
            });
        },
        [navigate, location.state.query, cardInfo, swiperIndex, linkOpenMenu]
    );

    const callbackIncomeDeductionWeb = useCallback(() => {
            clickIncomeDeductionWeb();
        },
        []
    );

    const viewLinkMenu = useCallback(() => {
            switch (linkOpenMenu) {
                case "monthlyFeeHistory" :
                    if (hasMonthlyFee && (monthlyFeeHistory.length !== 0 || nonInoutTotalBalance !== 0)) {
                        clickMonthlyFeeHistory();
                    }
                    break;
                case "monthlyFeeType" :
                    if (hasMonthlyFee && hasMonthlyFeeType) clickCardMonthlyFeeTypeView();
                    break;
                case "autoDebit" :
                    if (cardInfo.cardStatusCode !== '22') clickCardDebit();
                    break;
                case "password" :
                    if (cardInfo.cardStatusCode !== "22") clickPasswordChange();
                    break;
                case "freeze" :
                    if (cardInfo.cardStatusCode !== '22') clickFreeze();
                    break;
                case "report" :
                    if (cardInfo.cardStatusCode === '22') setLostCnclModal(true);
                    else setLostAcciModal(true);
                    break;
                case "reissue" :
                    if (cardInfo.reissueReceiveYn === 'N') openReissueModal(cardInfo);
                    break;
                case "terminate" :
                    clickTerminate();
                    break;
                case "monthlyFeeDebit" :
                    if (hasMonthlyFee) clickMonthlyFeeDebit();
                    break;
                case "yearEndTaxSettlement" :
                    if (
                        location.state.query.apiVersion != null
                        && incomeDeductionAppVersion != null
                        && parseInt(location.state.query.apiVersion) >= incomeDeductionAppVersion!
                    ) {
                        callbackIncomeDeductionWeb();
                    }
                    break;
            }
        }, [
            clickMonthlyFeeHistory, monthlyFeeHistory.length, nonInoutTotalBalance,
            clickCardMonthlyFeeTypeView, cardInfo, linkOpenMenu, clickCardDebit,
            clickFreeze, setLostCnclModal, setLostAcciModal, openReissueModal, clickTerminate,
            clickMonthlyFeeDebit, clickPasswordChange, location, incomeDeductionAppVersion,
            callbackIncomeDeductionWeb, hasMonthlyFee, hasMonthlyFeeType
        ]
    );

    // 최초실행
    useEffect(() => {
        console.log(cardInfo);
        console.log(location.state.query);

        inquiryStaticDataApi();
        cardIncomeDeductionVersionApi();
        callDebitState();
        callMonthlyFeeHistory(cardInfo);
        callNonInoutInfo();

        // 최초한번실행 useEffect() Warning 제거
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Swiper Height Resize
        swiperTabChangeEventHandle();
    }, [swiperTabChangeEventHandle]);

    useEffect(() => {
        if (isLinkMenu) {
            viewLinkMenu();
            setLinkMenu(false);
        }
    }, [isLinkMenu, viewLinkMenu]);

    useEffect(() => {
        if (linkOpenMenu === "monthlyFeeHistory") {
            if (monthlyFeeHistory.length !== 0 || nonInoutTotalBalance !== 0) {
                clickMonthlyFeeHistory();
            }
        }
    }, [linkOpenMenu, monthlyFeeHistory, nonInoutTotalBalance, clickMonthlyFeeHistory]);

    useEffect(() => {
        setHasMonthlyFee(cardInfo.membershipFeeCode === "1");
    }, [cardInfo.membershipFeeCode]);

    useEffect(() => {
        setHasMonthlyFeeType(cardInfo.monthlyFeePayTypeCnt > 0);
    }, [cardInfo.monthlyFeePayTypeCnt]);

    return (
        <section className="contents myPageSetting pdb75">
            <div className="inner">
                <div className="settingListWrap clfix">
                    <dl className="settingList">
                        {/* 카드 번호 */}
                        <CardManageButton title={t("SERV01.button12")}
                                          subcontent={cardInfo.cardNo}
                                          onClick={clickCardNumberView}
                        />
                        {/* 월회비 */}
                        {
                            hasMonthlyFee
                            && (<CardManageButton title={t("SERV01.button17")}
                                                  disabled={!hasMonthlyFeeType}
                                                  onClick={clickCardMonthlyFeeTypeView}
                                                  content={t("SERV01.button17-1")}
                            />)
                        }
                        {/* 카드 일시 정지 */}
                        <CardManageButton title={t("SERV01.button03")}
                                          disabled={cardInfo.cardStatusCode === "22"}
                                          onClick={clickFreeze}
                        />
                        {/* 카드 분실신고 */}
                        {
                            cardInfo.cardStatusCode === "22"
                                ? (<CardManageButton title={t("SERV01.button05")}
                                                     onClick={() => setLostCnclModal(true)}
                                />)
                                : (<CardManageButton title={t("SERV01.button04")}
                                                     onClick={() => setLostAcciModal(true)}
                                />)
                        }
                        {/* 카드 비밀번호 변경 */}
                        <CardManageButton title={t("SERV01.button02")}
                                          disabled={cardInfo.cardStatusCode === "22"}
                                          onClick={clickPasswordChange}
                        />
                        {/* 카드 재발급 */}
                        <CardManageButton title={t("SERV01.button06")}
                                          disabled={cardInfo.reissueReceiveYn === 'Y' || cardInfo.useYn !== "Y"}
                                          onClick={() => openReissueModal(cardInfo)}
                        />
                        {/* 카드 해지 */}
                        {
                            cardInfo.reqTerminateYn === "Y"
                                ? (<CardManageButton title={t("SERV01.button07_1")}
                                                     onClick={() => cardTerminateCancel(cardInfo)}
                                />) : (<CardManageButton title={t("SERV01.button07")} onClick={clickTerminate}/>)
                        }
                        {/* 카드 월회비 자동출금 */}
                        {
                            hasMonthlyFee
                            && (<CardManageButton title={t("SERV01.button13")} onClick={clickMonthlyFeeDebit}/>)
                        }
                        {/* 연말정산 다운로드 */}
                        {
                            (
                                location.state.query.apiVersion != null
                                && incomeDeductionAppVersion != null
                                && parseInt(location.state.query.apiVersion) >= incomeDeductionAppVersion!
                            ) && (<CardManageButton title={t("SERV01.button14")} onClick={clickIncomeDeductionWeb}/>)
                        }
                        {/* 연결계좌 */}
                        <CardManageButton title={t("SERV01.button01")}
                                          subcontent={"GmoneyPay Account"}
                                          onClick={clickGmoneypayDetailWeb}
                        />
                        {/* 카드 월회비 상세 */}
                        {
                            hasMonthlyFee
                            && (<CardManageButton title={t("SERV01.button15")}
                                                  disabled={monthlyFeeHistory.length === 0 && nonInoutTotalBalance === 0}
                                                  onClick={clickMonthlyFeeHistory}
                            />)
                        }
                        {/* 카드 자동출금 */}
                        <CardManageButton title={t("SERV01.button11")}
                                          disabled={cardInfo.cardStatusCode === "22"}
                                          onClick={clickCardDebit}
                        />
                    </dl>
                </div>
            </div>
            {/* 일시정지 */}
            <Modal isOpen={isPauseModal} contentLabel="modal" onRequestClose={closePauseModal} className="popTyLayer2"
                   overlayClassName="dim" closeTimeoutMS={0}
            >
                <p className="message">
                    {t(cardInfo.cardStatusCode === "21" ? "SERV03.popup05" : "SERV03.popup02")}
                </p>
                <dl>
                    <dd><span>{cardInfo.cardNo}</span></dd>
                </dl>
                <div className="btnWrapTy02">
                    <button type="button" className="btnPrimaryTy01" onClick={closePauseModal}>
                        <span>{t("COMMON.button.confirm")}</span>
                    </button>
                </div>
            </Modal>
            {/* 분실신고 등록 */}
            <Modal isOpen={isLostAcciModal} contentLabel="modal" onRequestClose={closeLostAcciModal}
                   className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}
            >
                <p className="message">{t("SERV04.popup02")}</p>
                <p className="subTxt mgt20">{t("SERV04.popup04")}</p>
                <p className="subTxt">{t("SERV04.popup05")}</p>
                {cardInfo.membershipFeeCode !== "0"
                    && (<p className="subTxt">{t("SERV04.popup07")}</p>)
                }
                <p className="messageARS">{t("SERV04.popup06")}</p>
                <div className="btnWrapTy02">
                    <button type="button" className="btnPrimaryTy01" onClick={closeLostAcciModal}>
                        <span>{t("COMMON.button.no")}</span>
                    </button>
                    <button type="button" className="btnPrimaryTy03" onClick={() => callAccident("111")}>
                        <span>{t("COMMON.button.yes")}</span>
                    </button>
                </div>
            </Modal>
            {/* 분실신고 해제 */}
            <Modal isOpen={isLostCnclModal} contentLabel="modal" onRequestClose={closeLostCnclModal}
                   className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}
            >
                <p className="message">{t("SERV05.popup02")}</p>
                <p className="subTxt mgt20">{t("SERV05.popup03")}</p>
                <p className="messageARS">{t("SERV05.popup06")}</p>
                <div className="btnWrapTy02">
                    <button type="button" className="btnPrimaryTy03" onClick={closeLostCnclModal}>
                        <span>{t("COMMON.button.no")}</span>
                    </button>
                    <button type="button" className="btnPrimaryTy01" onClick={clickLostCancel}>
                        <span>{t("COMMON.button.yes")}</span>
                    </button>
                </div>
            </Modal>
        </section>
    );
};

export default CardManage;